import { CacheProvider } from "@emotion/react";
import React, { useEffect, useRef, useState } from "react";
import { ThemeProvider as EmotionThemeProvider } from "@emotion/react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  CssBaseline,
  Step,
  Box,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
} from "@mui/material";
import createCache from "@emotion/cache";
import { styled } from "@mui/material/styles";

import rtlPlugin from "stylis-plugin-rtl";
import {
  EventNote as PlanningIcon,
  Campaign as OpenedIcon,
  Assessment as ClosedIcon,
  ThumbUp as AcceptedIcon,
  DoneAll as FinishedIcon,
} from "@mui/icons-material";
import { Stack } from "@mui/system";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { ROLE } from "@/types";

const colors = [
  "#7e57c2",
  "#7e57c2",
  "#7e57c2",
  "#7e57c2",
  "#7e57c2",
  "#7e57c2",
  "#7e57c2",
  "#7e57c2",
  "#7e57c2",
];

const StepIconContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#e0e0e0",
  width: 40,
  height: 40,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontWeight: "bold",
  fontSize: "0.75rem",
  textAlign: "center",
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    width: 30,
    height: 30,
  },
}));

const SvgIcon = styled("svg")(({ theme }) => ({
  fontSize: "14px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
  },
}));

interface TenderStepperProps {
  status: string;
}
const theme = createTheme({
  direction: "rtl",
});
const cacheRtl = createCache({
  key: "mui-rtl",
  stylisPlugins: [rtlPlugin],
});

export const TenderStepper: React.FC<TenderStepperProps> = ({ status }) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);

  const statuses = [
    {
      label: t("status.planning"),
      name: "Planning",
      icon: <PlanningIcon />,
      subText: t("Phases.phaseOne"),
    },
    {
      label: t("Phases.annons"),
      name: "Opened",
      icon: <OpenedIcon />,
      subText: t("Phases.publishDetails"),
    },
    {
      label: t("status.closed"),
      name: "Closed",
      icon: <ClosedIcon />,
      subText: t("Phases.reviewOffers"),
    },
    {
      label: t("status.accepted"),
      name: "Accepted",
      icon: <AcceptedIcon />,
      subText: t("Phases.selectOffer"),
    },

    {
      label: t("status.finished"),
      name: "Finished",
      icon: <FinishedIcon />,
      subText: t("Phases.completeProcess"),
    },
  ];
  const statusesSeller = [
    {
      label: t("Phases.annons"),
      name: "Opened",
      icon: <OpenedIcon />,
      subText: t("Phases.publishDetails"),
    },
    {
      label: t("status.closed"),
      name: "Closed",
      icon: <ClosedIcon />,
      subText: t("Phases.reviewOffers"),
    },
    {
      label: t("status.accepted"),
      name: "Accepted",
      icon: <AcceptedIcon />,
      subText: t("Phases.selectOffer"),
    },

    {
      label: t("status.finished"),
      name: "Finished",
      icon: <FinishedIcon />,
      subText: t("Phases.completeProcess"),
    },
  ];
  const [activeStep, setActiveStep] = useState(0);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const getFilteredStatuses = () => {
    if (authUser?.role === ROLE.BUYER) {
      return statuses;
    } else {
      return statusesSeller;
    }
  };

  const filteredStatuses = getFilteredStatuses();

  const stepperRootRef = useRef<HTMLDivElement>(null);
  const stepperRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (status) {
      const index = filteredStatuses.findIndex((item) => item.name === status);
      setActiveStep(index !== -1 ? index : 0); // Set to 0 if not found
    }
  }, [status, filteredStatuses]);

  const QontoConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
      [theme.breakpoints.up("sm")]: {
        top: "18px",
      },
      zIndex: 0,
      left: "calc(-50% + 16px)",
      right: "calc(50% + 16px)",
    },
    [`&.${stepConnectorClasses.active}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: "#784af4",
      },
    },
    [`&.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: "#784af4",
      },
    },
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: "#eaeaf0",
      borderTopWidth: 3,
      borderRadius: 1,
    },
  }));
  useEffect(() => {
    if (stepperRef.current && stepperRootRef.current) {
      const stepWidth =
        Number(stepperRootRef.current.offsetWidth) / filteredStatuses.length; // Calculate each step width
      const scrollPosition =
        stepWidth * activeStep - window.innerWidth / 2 + stepWidth / 2;
      const maxScroll =
        stepperRef.current.scrollWidth - stepperRef.current.clientWidth;
      const clampedScrollPosition = Math.max(
        0,
        Math.min(scrollPosition, maxScroll)
      );

      stepperRef.current.scrollTo({
        left: -clampedScrollPosition,
        behavior: "smooth",
      });
    }
  }, [activeStep, filteredStatuses.length]);
  return (
    <CacheProvider value={cacheRtl}>
      <EmotionThemeProvider theme={theme}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <div
            ref={stepperRef}
            className="overflow-scroll no-scrollbar"
            style={{ overflowX: "scroll", scrollBehavior: "smooth" }}
          >
            <Stack sx={{ minWidth: "600px" }}>
              <Stepper
                ref={stepperRootRef}
                alternativeLabel
                activeStep={activeStep}
                connector={<QontoConnector />}
              >
                {filteredStatuses.map((item, index) => (
                  <Step key={item.label}>
                    <StepLabel
                      StepIconComponent={() => {
                        return (
                          <StepIconContainer
                            sx={{
                              zIndex: 12,
                              backgroundColor:
                                activeStep >= index ? colors[index] : "#e0e0e0",
                            }}
                          >
                            <SvgIcon as={item.icon.type} />
                          </StepIconContainer>
                        );
                      }}
                    >
                      {item.label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Stack>
          </div>
        </ThemeProvider>
      </EmotionThemeProvider>
    </CacheProvider>
  );
};

export default TenderStepper;
